import type { DependencyCondition, DependencyMap, FieldDependencyState } from '@/types/fieldDependency';

/**
 * Evaluate a single dependency condition
 */
export function evaluateCondition(
    condition: DependencyCondition,
    actualValue: unknown,
    expectedValues: (string | number | boolean)[] | null,
): boolean {
    const expected = expectedValues ?? [];

    switch (condition) {
        case 'equals':
            return expected.some((v) => v === actualValue);

        case 'not_equals':
            return !expected.some((v) => v === actualValue);

        case 'in_array':
            if (Array.isArray(actualValue)) {
                return actualValue.some((v) => expected.includes(v));
            }
            return expected.includes(actualValue as string | number | boolean);

        case 'not_in_array':
            if (Array.isArray(actualValue)) {
                return !actualValue.some((v) => expected.includes(v));
            }
            return !expected.includes(actualValue as string | number | boolean);

        case 'is_empty':
            return (
                actualValue === null || actualValue === undefined || actualValue === '' || (Array.isArray(actualValue) && actualValue.length === 0)
            );

        case 'is_not_empty':
            return !(
                actualValue === null ||
                actualValue === undefined ||
                actualValue === '' ||
                (Array.isArray(actualValue) && actualValue.length === 0)
            );

        default:
            return false;
    }
}

/**
 * Evaluate all dependencies for a field and return the computed state
 */
export function evaluateFieldDependencies(
    fieldColumn: string,
    dependencyMap: DependencyMap,
    formValues: Record<string, unknown>,
    baseOptions: Array<{ label: string; value: string }> | null,
): FieldDependencyState {
    const dependencies = dependencyMap[fieldColumn];

    // Default state: visible, original options
    const state: FieldDependencyState = {
        visible: true,
        options: baseOptions,
    };

    if (!dependencies || dependencies.length === 0) {
        return state;
    }

    // Group dependencies by type
    const visibilityDeps = dependencies.filter((d) => d.type === 'visibility');
    const optionsDeps = dependencies.filter((d) => d.type === 'options');

    // Evaluate visibility dependencies (ALL must pass for field to be visible)
    if (visibilityDeps.length > 0) {
        state.visible = visibilityDeps.every((dep) => {
            const sourceValue = formValues[dep.sourceFieldColumn];
            return evaluateCondition(dep.condition, sourceValue, dep.expectedValues);
        });
    }

    // Evaluate options dependencies (first matching one wins, by priority)
    if (optionsDeps.length > 0 && state.visible) {
        for (const dep of optionsDeps) {
            const sourceValue = formValues[dep.sourceFieldColumn];
            if (evaluateCondition(dep.condition, sourceValue, dep.expectedValues)) {
                state.options = dep.filteredOptions ?? baseOptions;
                break;
            }
        }
    }

    return state;
}

/**
 * Create a dependency evaluator function for a form
 * Returns a function that computes all field states based on current form values
 */
export function createDependencyEvaluator(dependencyMap: DependencyMap, fieldBaseOptions: Record<string, Array<{ label: string; value: string }>>) {
    return function evaluateAll(formValues: Record<string, unknown>): Record<string, FieldDependencyState> {
        const states: Record<string, FieldDependencyState> = {};

        for (const fieldColumn of Object.keys(dependencyMap)) {
            states[fieldColumn] = evaluateFieldDependencies(fieldColumn, dependencyMap, formValues, fieldBaseOptions[fieldColumn] ?? null);
        }

        return states;
    };
}

/**
 * Parse options from field into consistent array format
 */
export function parseFieldOptions(options: unknown): Array<{ label: string; value: string }> | null {
    if (!options) return null;

    // Already an array of {label, value}
    if (Array.isArray(options)) {
        return options.map((opt) => {
            if (typeof opt === 'object' && opt !== null && 'label' in opt && 'value' in opt) {
                return { label: String(opt.label), value: String(opt.value) };
            }
            return { label: String(opt), value: String(opt) };
        });
    }

    // Object format: { "Label": "value" }
    if (typeof options === 'object' && options !== null) {
        return Object.entries(options).map(([label, value]) => ({
            label,
            value: String(value),
        }));
    }

    return null;
}
